import { useGetFactorLimitsQuery } from '@/app/api';
import { Currency, FactorLimitStatus } from '@/app/api/types';
import useFilters from '@/app/hooks/useFilters';
import { UITable } from '@/shared/ui/ui-table';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import PenLineIcon from '@/app/assets/icons/pen-line.svg?react';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import SearchIcon from '@/app/assets/icons/search-alt.svg?react';
import XmarkIcon from '@/app/assets/icons/xmark.svg?react';
import { EmptyDataPlaceholder } from '@/shared/ui/empty-data-placeholder';
import { triada } from '@/app/helpers';
import UIAutocomplete from '@/shared/ui/ui-autocmplete';
import { CURRENCY_OPTIONS } from '@/constants';

interface Props {
  status?: FactorLimitStatus;
  setLimitsCount: Dispatch<
    SetStateAction<
      | {
          active: number;
          overdue: number;
          suspended: number;
        }
      | undefined
    >
  >;
}

export const FactorLimitsTable: FC<Props> = ({ status, setLimitsCount }) => {
  const { t } = useTranslation();
  const { nonEmptyFilters: filters, updateFilters } = useFilters({
    defaultFilters: {
      page: 1,
      pageSize: 20,
      search: '',
      currency: 'UZS',
    },
    saveToUrl: true,
  });
  const { data } = useGetFactorLimitsQuery({
    ...filters,
    status,
    page: Number(filters.page),
    pageSize: Number(filters.pageSize),
  });

  useEffect(() => {
    setLimitsCount(data?.limitsCount);
  }, [data]);

  const [searchValue, setSearchValue] = useState(filters.search || '');

  const handleSearch = useMemo(
    () =>
      _.debounce((value: string) => {
        updateFilters({ search: value });
      }, 300),
    [updateFilters]
  );

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  const handleClearSearch = () => {
    handleSearch.cancel();
    setSearchValue('');
    updateFilters({ search: '' });
  };

  return (
    <>
      <Stack direction='row' spacing={2}>
        <TextField
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            handleSearch(e.target.value);
          }}
          placeholder={t('tin_or_organization_name')}
          sx={{ flex: 1 }}
          InputProps={{
            endAdornment: searchValue ? (
              <IconButton size='small' onClick={handleClearSearch}>
                <XmarkIcon width={14} height={14} />
              </IconButton>
            ) : (
              <SearchIcon />
            ),
          }}
        />
        <UIAutocomplete
          value={CURRENCY_OPTIONS.find(
            (item) => item.value === filters.currency
          )}
          options={CURRENCY_OPTIONS}
          getOptionLabel={(option) => {
            return t(option.label);
          }}
          TextFieldProps={{
            sx: { width: 125 },
          }}
          onChange={(_event, value) => {
            updateFilters({ currency: value?.value as Currency });
          }}
          disableClearable
        />
        <Box component={Link} to='/factor/limits/clients'>
          <Button sx={{ height: 40 }} color='inherit'>
            {t('add_client')}
          </Button>
        </Box>
      </Stack>
      <EmptyDataPlaceholder
        isEmpty={!data?.data?.length}
        title={t('no_data_with_this_filter')}
      >
        <UITable
          sx={{ mt: 2 }}
          data={data?.data || []}
          columns={[
            {
              label: t('name'),
              render: ({ company }) => (
                <Box>
                  <Typography fontSize='inherit' fontWeight='500'>
                    {company.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'primary.main' }}>
                    {company.tin}
                  </Typography>
                </Box>
              ),
            },
            {
              label: t('branch'),
              render: ({ branch }) => (
                <Typography fontSize={13} fontWeight={400}>
                  {branch?.name}
                </Typography>
              ),
            },
            {
              label: t('status'),
              render: ({ status }) => (
                <Stack direction='row' spacing={0.5}>
                  {(() => {
                    switch (status) {
                      case 'active':
                        return <Chip label={t('active')} color='success' />;
                      case 'overdue':
                        return <Chip label={t('overdue')} color='error' />;
                      case 'suspended':
                        return <Chip label={t('suspended')} color='info' />;
                      case 'generalAgreementSignatureExpected':
                        return (
                          <Chip
                            label={t(
                              'statuses.factor.generalAgreementSignatureExpected'
                            )}
                            sx={{
                              backgroundColor: '#FFEA00',
                              color: '#0B1370',
                            }}
                          />
                        );
                      case 'generalAgreementWasRejected':
                        return (
                          <Chip
                            label={t(
                              'statuses.factor.generalAgreementWasRejected'
                            )}
                            color='error'
                          />
                        );
                      case 'generalAgreementExpired':
                        return (
                          <Chip
                            label={t('statuses.factor.generalAgreementExpired')}
                            color='error'
                          />
                        );
                      default:
                        break;
                    }
                  })()}
                </Stack>
              ),
            },
            {
              label: t('limit_sum'),
              render: ({ allowedLimit }) => (
                <Typography fontWeight={600} fontSize={13}>
                  {triada((allowedLimit || 0)?.toFixed(2))}
                </Typography>
              ),
              align: 'right',
              headerAlign: 'right',
            },
            {
              label: t('available'),
              render: ({ availableLimit }) => (
                <Typography fontWeight={600} fontSize={13}>
                  {triada((availableLimit || 0)?.toFixed(2))}
                </Typography>
              ),
              align: 'right',
              headerAlign: 'right',
            },
            {
              label: t('used'),
              render: ({ usedLimit }) => (
                <Typography fontWeight={600} fontSize={13}>
                  {triada((usedLimit || 0)?.toFixed(2))}
                </Typography>
              ),
              align: 'right',
              headerAlign: 'right',
            },
            {
              label: t('deals'),
              render: ({ completedDealsAmount, redeemedDealsAmount }) =>
                `${redeemedDealsAmount || 0}/${completedDealsAmount || 0}`,
              align: 'right',
              headerAlign: 'right',
            },
            {
              render: ({ unique }) => (
                <Link to={`/factor/limits/${unique}`}>
                  <IconButton>
                    <PenLineIcon />
                  </IconButton>
                </Link>
              ),
              nowrap: true,
            },
          ]}
          PaginationProps={{
            count: data?.totalPages || 0,
            page: Number(filters.page),
            onChange: (_, page) => updateFilters({ page }),
          }}
        />
      </EmptyDataPlaceholder>
    </>
  );
};
